import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {clone} from "lodash";
import {Entity} from "./entity";
import {adjustFromTimeZoneSafely} from "../date/date";
import {EntitySearchCriteria} from "./search/entity-search-criteria";
import {toUSStateCode, USStateCode} from "./us-state-code";
import type {FeatureCollection} from "geojson";

export type RestrictionType = DatabaseCode;

const hunting: RestrictionType = {
  code: 'HUNTING',
  displayValue: {
    id: 'common.entity.restriction.type.hunting',
    defaultMessage: 'Hunting',
  },
};

const weather: RestrictionType = {
  code: 'WEATHER',
  displayValue: {
    id: 'common.entity.restriction.type.weather',
    defaultMessage: 'Weather',
  },
};

const road: RestrictionType = {
  code: 'ROAD',
  displayValue: {
    id: 'common.entity.restriction.type.road',
    defaultMessage: 'Road',
  },
};

const rejection: RestrictionType = {
  code: 'REJECTION',
  displayValue: {
    id: 'common.entity.restriction.type.rejection',
    defaultMessage: 'Rejection',
  },
};

export const restrictionType = {
  hunting,
  weather,
  road,
  rejection,
};

export const restrictionTypes = Object.values(restrictionType);

export const toRestrictionType = (code: string): RestrictionType => toDatabaseCode(code, restrictionTypes)

export const RestrictionScheduleWindowValidator = t.intersection([
  t.type({
    type: t.string,
    startTime: t.number,
    endTime: t.number,
  }),
  t.partial({
    id: t.string,
    restrictionId: t.string,
    daysOfWeek: t.array(t.string),
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export type RestrictionScheduleWindow = t.TypeOf<typeof RestrictionScheduleWindowValidator>;

@Exclude()
export class RestrictionScheduleWindowEntity {
  @Expose()
  id?: string;

  @Expose()
  restrictionId?: string;

  @Expose()
  type!: string;

  @Expose()
  startTime!: number;

  @Expose()
  endTime!: number;

  @Expose()
  daysOfWeek?: string[];

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;
}

export const RestrictionValidator = t.intersection([
  t.type({
    id: t.string,
    type: t.string,
  }),
  t.partial({
    objectId: t.string,
    objectType: t.string,
    objectName: t.string,
    companyId: t.string,
    companyName: t.string,
    county: t.string,
    state: t.string,
    boundary: t.any,
    startDttm: t.number,
    endDttm: t.number,
    applyScheduleWindows: t.boolean,
    scheduleWindows: t.array(RestrictionScheduleWindowValidator),
    description: t.string,
    displayName: t.string,
    lastModifiedById: t.string,
    createById: t.string,
    createByName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    regionIds: t.array(t.string),
  }),
]);

export const RestrictionArrayValidator = t.array(RestrictionValidator);

export type Restriction = t.TypeOf<typeof RestrictionValidator>;
export type RestrictionArray = t.TypeOf<typeof RestrictionArrayValidator>;

@Exclude()
export class RestrictionEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId?: string;

  @Expose()
  objectType?: string;

  @Expose()
  objectName?: string;

  @Expose()
  companyId?: string;

  @Expose()
  companyName?: string;

  @Expose()
  county?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toUSStateCode))
  state?: USStateCode;

  @Expose()
  boundary?: FeatureCollection;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toRestrictionType))
  type!: RestrictionType;

  @Expose()
  startDttm?: number;

  @Expose()
  endDttm?: number;

  /** When false the schedule windows are informational and the restriction applies all day. */
  @Expose()
  applyScheduleWindows?: boolean;

  @Expose()
  @Type(() => RestrictionScheduleWindowEntity)
  scheduleWindows?: RestrictionScheduleWindowEntity[];

  @Expose()
  description?: string;

  @Expose({name: 'displayName'})
  displayableName?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createById?: string;

  @Expose()
  createByName?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  regionIds?: string[]

  readonly entityType = 'SITE_RESTRICTION';

  get displayName(): Readonly<string> {
    return this.displayableName ?? "Unknown";
  }

  /**
   * Constructor for RestrictionEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(plain: Restriction): RestrictionEntity {
    return plainToInstance(RestrictionEntity, plain, {
      excludeExtraneousValues: true,
    });
  }

  loadPlainValue(): Promise<Restriction> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RestrictionValidator, object);
  }

  asPlainValue(): Restriction {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(RestrictionValidator, object);
  }

  clone(): RestrictionEntity {
    return clone(this);
  }

  isActive = (dttm: number = new Date().getTime()): boolean => {
    const adjustedStartDttm = adjustFromTimeZoneSafely(this.startDttm)?.getTime()
    const adjustedEndDttm = adjustFromTimeZoneSafely(this.endDttm)?.getTime()

    const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= dttm
    const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > dttm

    if (!meetsStartThresh || !meetsEndThresh) {
      return false
    }

    if (this.applyScheduleWindows === false || !this.scheduleWindows || this.scheduleWindows.length === 0) {
      return true
    }

    return this.scheduleWindows.some(window => this.isScheduleWindowActive(window, dttm))
  }

  private isScheduleWindowActive = (window: RestrictionScheduleWindowEntity, dttm: number): boolean => {
    const scheduleStart = adjustFromTimeZoneSafely(window.startTime)
    const scheduleEnd = adjustFromTimeZoneSafely(window.endTime)
    if (!scheduleStart || !scheduleEnd) {
      return false
    }

    const target = new Date(dttm)
    const targetTime = target.getHours() * 60 + target.getMinutes()
    const startTime = scheduleStart.getHours() * 60 + scheduleStart.getMinutes()
    const endTime = scheduleEnd.getHours() * 60 + scheduleEnd.getMinutes()
    const overnight = endTime <= startTime
    const days = new Set(window.daysOfWeek)
    const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
    const today = dayNames[target.getDay()]
    const yesterday = dayNames[(target.getDay() + 6) % 7]

    if (window.type === 'WEEKLY' && days.size === 0) {
      return false
    }

    if (!overnight) {
      const matchesDay = window.type !== 'WEEKLY' || days.has(today)
      return matchesDay && targetTime >= startTime && targetTime < endTime
    }

    const matchesToday = window.type !== 'WEEKLY' || days.has(today)
    const matchesYesterday = window.type !== 'WEEKLY' || days.has(yesterday)
    return (matchesToday && targetTime >= startTime) || (matchesYesterday && targetTime < endTime)
  }
}

export interface RestrictionSearchCriteria {
  dttm?: number
  fromDttm?: number
  toDttm?: number
  objectIds?: string[]
  companyIds?: string[]
  types?: string[]
  county?: string
  state?: string
  boundary?: FeatureCollection
  regionIds?: string[]
  objectHierarchy?: boolean

  // ignore
  entityCriteria?: EntitySearchCriteria
}
